import { FC, useState } from "react";
import { Link } from "react-router-dom";
import {
    Button,
    DataTableActionButton,
    DataTableActions,
    DataTableEntityCell,
    Modal,
    StatusDot,
    StatusDotColor,
    Tag,
    Tooltip,
    message,
} from "../../../design-system";
import {
    BriefStatus,
    BuyerBrief,
    LegacyBriefListItem,
    STATUS_LABELS,
    Verdict,
    VERDICT_LABELS,
} from "../../../types/BuyerBriefTypes";
import { runBrief } from "../../../common/api/BuyerBriefClient";
import { SvgIcon } from "../../../common/components/SvgIcon";
import { useUserState } from "../../../common/UserContext";
import { isSuperAdmin } from "../../../types/UserGroup";
import classes from "./BriefCells.module.less";

const STATUS_COLORS: Record<BriefStatus, StatusDotColor> = {
    [BriefStatus.QUEUED]: "grey",
    [BriefStatus.PROCESSING]: "blue",
    [BriefStatus.COMPLETED]: "green",
    [BriefStatus.FAILED]: "red",
};

const VERDICT_COLORS: Partial<Record<Verdict, string>> = {
    [Verdict.STRONG_YES]: "green",
    [Verdict.YES]: "green",
    [Verdict.CONDITIONAL]: "gold",
    [Verdict.RENEGOTIATE]: "orange",
    [Verdict.INSUFFICIENT_DATA]: "gold",
    [Verdict.NO]: "red",
    [Verdict.STRONG_NO]: "magenta",
};

const initial = (name: string) => (name || "?").charAt(0).toUpperCase();

/**
 * The supplier with its logo; the name opens the brief. Under it, the vendors
 * icon: blue and leading to the vendor's page when the brief is linked to one
 * of the org's vendors, grey otherwise. The index filters on that link, so it
 * has to be legible on the row.
 */
export const BriefSupplierCell: FC<{ brief: BuyerBrief }> = ({ brief }) => (
    <DataTableEntityCell
        nameSize="large"
        imageSrc={brief.logoUrl}
        imageFallback={initial(brief.supplierName)}
        name={<Link to={`/briefs/${brief.id}`}>{brief.supplierName}</Link>}
        detail={
            brief.vendorKey ? (
                <Tooltip title="Linked to one of your vendors">
                    <Link
                        to={`/vendor/${brief.vendorKey}`}
                        className={classes.vendor}
                        aria-label="Linked to one of your vendors"
                    >
                        <SvgIcon name="dataset" size={14} />
                    </Link>
                </Tooltip>
            ) : (
                <Tooltip title="Not linked to a vendor">
                    <span
                        className={`${classes.vendor} ${classes["vendor--unlinked"]}`}
                        aria-label="Not linked to a vendor"
                    >
                        <SvgIcon name="dataset" size={14} />
                    </span>
                </Tooltip>
            )
        }
    />
);

/** A pre-V2 brief's supplier; the name opens the legacy renderer. */
export const LegacyBriefSupplierCell: FC<{ brief: LegacyBriefListItem }> = ({ brief }) => (
    <DataTableEntityCell
        nameSize="large"
        imageSrc={brief.logoUrl}
        imageFallback={initial(brief.supplierName)}
        name={<Link to={`/supplier/${brief.id}`}>{brief.supplierName}</Link>}
    />
);

/**
 * Where the brief stands: a status dot, with the pipeline's current phase
 * under it while it generates (in warning tone when it has stalled). A super
 * admin can open a failed brief's error details from here.
 */
export const BriefStatusCell: FC<{ brief: BuyerBrief }> = ({ brief }) => {
    const { me } = useUserState();
    const superAdmin = isSuperAdmin(me.roles || []);
    const [errorOpen, setErrorOpen] = useState(false);

    const stalled = brief.isStuck ?? false;
    const failed = brief.status === BriefStatus.FAILED;
    const status = stalled ? (
        <StatusDot color="orange">Stalled</StatusDot>
    ) : (
        <StatusDot color={STATUS_COLORS[brief.status]}>{STATUS_LABELS[brief.status]}</StatusDot>
    );

    return (
        <div className={classes.status}>
            {failed && superAdmin ? (
                <button type="button" className={classes.errorTrigger} onClick={() => setErrorOpen(true)}>
                    {status}
                </button>
            ) : (
                status
            )}
            {/* Server only sends this while genuinely mid-pipeline. */}
            {brief.currentStep && (
                <span
                    className={stalled ? classes.stalledPhase : classes.phase}
                    title={brief.currentStep}
                >
                    {stalled ? `Stalled at: ${brief.currentStep}` : brief.currentStep}
                </span>
            )}
            {failed && superAdmin && (
                <Modal
                    title={`Error: ${brief.supplierName}`}
                    open={errorOpen}
                    onCancel={() => setErrorOpen(false)}
                    footer={<Button onClick={() => setErrorOpen(false)}>Close</Button>}
                >
                    <pre className={classes.errorDetails}>
                        {brief.errorDetails?.message ||
                            JSON.stringify(brief.errorDetails, null, 2) ||
                            "No error details available."}
                    </pre>
                </Modal>
            )}
        </div>
    );
};

/** The model's recommendation in its own words, coloured by the verdict. */
export const BriefRecommendationCell: FC<{ brief: BuyerBrief }> = ({ brief }) =>
    brief.verdict ? (
        <Tag color={VERDICT_COLORS[brief.verdict] || "default"}>
            {brief.recommendationHeadline || VERDICT_LABELS[brief.verdict]}
        </Tag>
    ) : (
        <>–</>
    );

interface BriefActionsProps {
    brief: BuyerBrief;
    onDelete: (brief: BuyerBrief) => void;
    /** Called once a re-run was accepted, so the list reloads. */
    onReRun: () => void;
}

/** Re-run (a failed or stalled brief) and delete. */
export const BriefActions: FC<BriefActionsProps> = ({ brief, onDelete, onReRun }) => {
    const [reRunning, setReRunning] = useState(false);
    // QUEUED is excluded on purpose: the brief is already waiting on a worker,
    // so re-running only costs it its place in line. The server rejects it too.
    const canReRun = brief.status === BriefStatus.FAILED || (brief.isStuck ?? false);

    const handleReRun = async () => {
        setReRunning(true);
        try {
            await runBrief(brief.id);
            message.success("Brief re-run started");
            onReRun();
        } catch (err: any) {
            message.error(typeof err?.misc === "string" ? err.misc : "Failed to start re-run");
        } finally {
            setReRunning(false);
        }
    };

    return (
        <DataTableActions>
            {canReRun && (
                <DataTableActionButton
                    icon="progress"
                    label="Re-run brief"
                    disabled={reRunning}
                    onClick={handleReRun}
                />
            )}
            <DataTableActionButton icon="trash" label="Delete brief" onClick={() => onDelete(brief)} />
        </DataTableActions>
    );
};
